import { Box, Typography } from '@mui/material';
import React, { useRef } from 'react';
import CustomerNameTagButton from '../../components/CustomerNameTagButton';
import {
  BooleanInput,
  Create,
  DateInput,
  FormDataConsumer,
  Labeled,
  ReferenceInput,
  SelectInput,
  SimpleForm,
  TextInput,
  TimeInput,
  maxLength,
  maxValue,
  minValue,
  required
} from 'react-admin';
import Permission from '../../components/Permission';
import { RichTextInputCustom } from '../../components/RichTextInputCustom';
import { MAX_DATE, PERMISSIONS } from '../../constants';
import dayjs from 'dayjs';

const richTextSx = {
  marginTop: '16px',
  width: 'auto',
  '& .ql-editor': {
    minHeight: 200
  },
};

const charCounterSx = {
  right: '1%',
  width: 'auto',
  height: '100%',
  fontSize: 12,
  opacity: .75,
  fontFamily: 'Helvetica',
  textAlign: 'end' as const,
};

const createMarketingCampaigns: React.FC = props => {
  const now = dayjs().toISOString();
  const nowDate = dayjs().toJSON().slice(0, 10);
  const MAX_LENGTH_PUSH = 130;
  const titleInputRef = useRef<HTMLInputElement>(null);
  const pushInputRef = useRef<HTMLInputElement>(null);
  const transform = (data) => ({
    ...data,
    startDate: dayjs(data.startDate).toISOString(),
    endDate: dayjs(data.endDate).toISOString()
  })

  return (
    <Permission permission={PERMISSIONS.MARKETING_CAMPAIGNS}>
      <Create {...props} title="Criar campanha de marketing" transform={transform}>
        <SimpleForm shouldUnregister>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextInput source="title" label="Título" sx={{ minWidth: 400 }} InputProps={{ inputRef: titleInputRef }} />
            <Box sx={{ mt: 2 }}>
              <CustomerNameTagButton inputRef={titleInputRef} />
            </Box>
          </Box>
          <ReferenceInput source="marketingCampaignId" reference="chains/chainId/marketing-campaigns" label="Campanha">
            <SelectInput
              label="Campanha"
              source="marketingCampaignId"
              optionText="label"
              validate={required()}
              sx={{ minWidth: 400 }}
            />
          </ReferenceInput>
          <FormDataConsumer>
            {({ formData }) => {
              return (
                <Box>
                  <DateInput
                    source="startDate"
                    label="Início da campanha"
                    defaultValue={dayjs().startOf('day').toISOString()}
                    validate={[
                      required(),
                      minValue(nowDate, 'A data de início da campanha deve ser no mínimo a data atual'),
                      maxValue(formData.endDate, 'A data de início da campanha deve ser no máximo a data de término')
                    ]}
                    inputProps={{ max: MAX_DATE }}
                    style={{ marginRight: 16 }}
                  />
                  {!formData.alwaysActive &&
                    <DateInput
                      source="endDate"
                      label="Fim da campanha"
                      inputProps={{ max: MAX_DATE }}
                      validate={[required(), minValue(
                        formData.startDate,
                        'A data de término da campanha deve ser no mínimo a data de início',
                      )]}
                    />}
                </Box>
              )
            }}
          </FormDataConsumer>
          <BooleanInput
            label="Sempre ativa"
            source="alwaysActive"
            defaultValue={false}
          />
          <FormDataConsumer>
            {({ formData }) => formData.alwaysActive &&
              <Typography variant="subtitle2" gutterBottom style={{ marginTop: -16, marginBottom: 16 }}>
                *A campanha não terá uma data de término.
              </Typography>
            }
          </FormDataConsumer>
          <TimeInput
            label="Horário de Envio"
            source="time"
            variant="filled"
            defaultValue={now}
            sx={{ width: 160, contain: 'content' }}
            required
          />
          <Box sx={richTextSx}>
            <Labeled label="Descrição: ">
              <RichTextInputCustom
                source="body"
                label="Essa mensagem pode conter formatações e será exibida no aplicativo, na tela de notificações. Ela NÃO será enviada na notificação PUSH"
                validate={required()}
                fieldType="marketing_campaign_body"
              />
            </Labeled>
          </Box>
          <Labeled label="Enviar via: ">
            <>
              {/* <BooleanInput source="sms" label="SMS" /> */}
              <BooleanInput defaultValue source="pushNotification" label="Notificação no aplicativo" />
            </>
          </Labeled>
          <FormDataConsumer>
            {({ formData }) => formData.pushNotification &&
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                <Labeled label="Esta mensagem será enviada na notificação Push:" sx={{ width: '70%' }}>
                  <>
                    <TextInput
                      style={{ width: '100%' }}
                      source="pushMessage"
                      label="Mensagem Push"
                      validate={[required(), maxLength(MAX_LENGTH_PUSH)]}
                      InputProps={{ autoComplete: 'off', inputRef: pushInputRef }}
                      helperText={false}
                    />
                    <Box component="span" sx={charCounterSx}>
                      {formData.pushMessage?.length || 0}/{MAX_LENGTH_PUSH}
                    </Box>
                  </>
                </Labeled>
                <Box sx={{ mt: 5 }}>
                  <CustomerNameTagButton inputRef={pushInputRef} />
                </Box>
              </Box>
            }
          </FormDataConsumer>
          <Typography variant="subtitle2" gutterBottom style={{ marginTop: 16, width: '100%' }}>
            * As campanhas criadas e iniciadas HOJE serão enviadas a partir do dia seguinte.
          </Typography>
        </SimpleForm>
      </Create>
    </Permission >
  )
}

export default createMarketingCampaigns;